/**
 * PL/SQL Editor tab content types and factory.
 *
 * Extends the debugger content model with edit-mode fields (source views,
 * compilation state, member navigation). The unified tab supports both
 * editing and debugging PL/SQL objects.
 */

import type {
  DebugBreakpoint,
  DebugVariable,
  DebugStackFrame,
  OracleDebuggerCompilationError,
  OracleProfilerLineData,
  OracleProfilerUnitSummary,
} from '../../../types/electron';

import type { PackageMemberParam } from '../core/types/workspaceTypes';
import type { ProgramIdentity, PlSqlDebugState, ProfilingState, PlSqlDebuggerTabContent } from '../pl-sql-debugger/types';

// ---------------------------------------------------------------------------
// Core types
// ---------------------------------------------------------------------------

/** Object type for the PL/SQL editor (same as debugger) */
export type PlSqlEditorObjectType = 'procedure' | 'function' | 'package' | 'trigger' | 'anonymous';

/** Compilation error reported by Oracle */
export type CompilationError = {
  line: number;
  column: number;
  text: string;
  severity: 'ERROR' | 'WARNING';
};

/** Content for PL/SQL editor tabs — extends debugger content with edit-mode fields */
export type PlSqlEditorTabContent = {
  // --- Identifying information ---
  objectName: string;
  schema: string;
  database: string;
  objectType: PlSqlEditorObjectType;

  // --- Edit-mode fields ---
  /** Current mode: edit (read/write source) or debug (step-through) */
  mode: 'edit' | 'debug';
  /** Which source view is displayed (spec or body) — packages only */
  sourceView: 'spec' | 'body';
  /** Package specification source */
  specSource?: string;
  /** Package body source */
  bodySource?: string;
  /** Whether the displayed source has been modified since last compile/load */
  sourceModified: boolean;
  /** Whether spec source has been modified (persists across view toggles) */
  specModified?: boolean;
  /** Whether body source has been modified (persists across view toggles) */
  bodyModified?: boolean;
  /** Whether the object was compiled with debug symbols */
  compiledWithDebug: boolean;
  /** Whether the member navigator sidebar is open */
  memberNavOpen: boolean;
  /** View mode: source editor or dependency graph — packages only */
  viewMode?: 'source' | 'dependencies';
  /** AI Chat session ID for resuming conversations (persisted) */
  aiChatSessionId?: string;
  /** AI Chat sidebar open state (persisted) */
  aiChatOpen?: boolean;
  /** Whether the spec is in INVALID status */
  specInvalid?: boolean;
  /** Whether the body is in INVALID status */
  bodyInvalid?: boolean;
  /** Compilation errors from last compile */
  compilationErrors?: CompilationError[];

  // --- Debug-mode fields (inherited from PlSqlDebuggerTabContent) ---
  /** PL/SQL source code (the source shown in Monaco during debug) */
  source?: string;
  /** Debug session key (from backend) */
  sessionKey?: string;
  /** Current debug state */
  debugState: PlSqlDebugState;
  /** Current execution line (when paused) */
  currentLine?: number;
  /** Breakpoints for the currently displayed program */
  breakpoints: DebugBreakpoint[];
  /** Per-program breakpoint storage keyed by programKey */
  breakpointsByProgram: Record<string, DebugBreakpoint[]>;
  /** Currently displayed program identity (differs when stepping into another routine) */
  displayedProgram?: ProgramIdentity;
  /** Variables (populated when paused) */
  variables: DebugVariable[];
  /** Call stack frames */
  callStack: DebugStackFrame[];
  /** Messages (output, errors) */
  messages: Array<{ type: 'info' | 'error' | 'output'; content: string; timestamp: string }>;
  /** Watch expressions */
  watchExpressions: Array<{ id: string; expression: string; value?: string | null; error?: string }>;
  /** Exception breakpoint settings */
  exceptionBreakpoints: {
    breakOnAll: boolean;
    namedExceptions: string[];
  };
  /** Debug protocol used by the active session ('dbms_debug' or 'jdwp') */
  debugProtocol?: 'dbms_debug' | 'jdwp';
  /** Error message */
  error?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Selected package member for debug launch */
  targetMember?: { name: string; type: 'procedure' | 'function'; overloadIndex?: number; params?: PackageMemberParam[]; returnType?: string };
  /** Package members list */
  members?: Array<{ name: string; type: 'procedure' | 'function'; overloadIndex?: number; params?: PackageMemberParam[]; returnType?: string }>;
  /** Last PL/SQL block used to start a debug session */
  lastLaunchBlock?: string;

  // --- Profiling fields (inherited from PlSqlDebuggerTabContent) ---
  profilingState: ProfilingState;
  profileKey?: string;
  profileRunId?: number;
  profileLines?: OracleProfilerLineData[];
  profileUnits?: OracleProfilerUnitSummary[];
  profileTotalRunTime?: number;
  profileExecutionError?: string;

  // --- Compilation errors (from debug compilation) ---
  debugCompileErrors?: OracleDebuggerCompilationError[];
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/** Create default content for a PL/SQL editor tab */
export function createDefaultPlSqlEditorContent(
  objectName: string,
  schema: string,
  database: string,
  objectType: PlSqlEditorObjectType = 'procedure',
): PlSqlEditorTabContent {
  return {
    objectName,
    schema,
    database,
    objectType,
    // Edit-mode defaults
    mode: 'edit',
    sourceView: 'body',
    sourceModified: false,
    compiledWithDebug: false,
    memberNavOpen: objectType === 'package',
    // Debug-mode defaults
    debugState: 'idle',
    breakpoints: [],
    breakpointsByProgram: {},
    variables: [],
    callStack: [],
    messages: [],
    watchExpressions: [],
    exceptionBreakpoints: { breakOnAll: false, namedExceptions: [] },
    isLoading: true,
    profilingState: 'idle',
  };
}

// ---------------------------------------------------------------------------
// Compile-time structural assertion: PlSqlEditorTabContent must be a superset
// of PlSqlDebuggerTabContent so debug hooks can safely cast between them.
// ---------------------------------------------------------------------------
type _AssertSuperset = PlSqlDebuggerTabContent extends Pick<PlSqlEditorTabContent, keyof PlSqlDebuggerTabContent> ? true : never;
const _assertSuperset: _AssertSuperset = true;
void _assertSuperset;

// ---------------------------------------------------------------------------
// Imperative handle for forwardRef usage
// ---------------------------------------------------------------------------

/**
 * Imperative handle exposed by PlSqlEditorTab via forwardRef.
 *
 * Used by host components (e.g., Package Explorer) that embed the editor
 * and need to check unsaved-state before swapping the mounted content.
 */
export type PlSqlEditorTabHandle = {
  /** Returns whether the editor has unsaved changes (spec, body, or generic source) */
  getDirtyState(): { dirty: boolean; reasons: string[] };
};
